import { ChevronDown } from 'lucide-react';
import {
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
} from 'react';
import { useTranslation } from 'react-i18next';
import { filterOptions, groupOptions, nextEnabled, type SearchOption } from './search-select.ts';
import { Badge, Input } from './ui.tsx';

export type { SearchOption } from './search-select.ts';

const LIST_HEIGHT = 288;

/**
 * Campo de escolha com busca, para listas longas (cargos, centros de custo, gestores). Lista
 * flutuante que não é cortada pela rolagem das janelas. Teclado: setas andam, Enter escolhe, Esc
 * fecha sem fechar a janela. Leitores de tela o ouvem como combobox, com o rótulo do Field.
 */
export function SearchSelect({
  value,
  options,
  onChange,
  noneLabel,
  placeholder,
  disabled,
  required,
  id,
  ariaLabel,
  className,
}: {
  value: string;
  options: readonly SearchOption[];
  onChange: (id: string) => void;
  /** Rótulo do item que limpa o campo; sem ele, o campo não pode voltar a ficar vazio. */
  noneLabel?: string;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  id?: string;
  ariaLabel?: string;
  className?: string;
}) {
  const { t } = useTranslation();
  const listId = useId();
  const input = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [place, setPlace] = useState<CSSProperties>({});
  const all = useMemo(
    () => (noneLabel ? [{ id: '', label: noneLabel }, ...options] : options),
    [noneLabel, options],
  );
  const shown = useMemo(() => filterOptions(all, query), [all, query]);
  const selected = all.find((o) => o.id === value && o.id !== '');

  useLayoutEffect(() => {
    if (!open) return undefined;
    const update = () => {
      const box = input.current?.getBoundingClientRect();
      if (!box) return;
      const below = window.innerHeight - box.bottom;
      const up = below < 200 && box.top > below;
      setPlace({
        left: box.left,
        width: box.width,
        maxHeight: Math.min(LIST_HEIGHT, (up ? box.top : below) - 12),
        ...(up ? { bottom: window.innerHeight - box.top + 4 } : { top: box.bottom + 4 }),
      });
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [open]);

  const openList = () => {
    if (disabled || open) return;
    setQuery('');
    setActive(
      Math.max(
        all.findIndex((o) => o.id === value),
        -1,
      ),
    );
    setOpen(true);
  };
  const choose = (option: SearchOption) => {
    if (option.disabled) return;
    setOpen(false);
    setQuery('');
    if (option.id !== value) onChange(option.id);
  };
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (open) setActive(nextEnabled(shown, active, e.key === 'ArrowDown' ? 1 : -1));
      else openList();
    } else if (e.key === 'Enter') {
      // Enter nunca envia o formulário a partir deste campo, como numa caixa de seleção.
      e.preventDefault();
      const option = shown[active];
      if (open && option) choose(option);
      else openList();
    } else if (e.key === 'Escape' && open) {
      e.stopPropagation();
      setOpen(false);
      setQuery('');
    } else if (e.key === 'Tab') {
      setOpen(false);
      setQuery('');
    }
  };

  return (
    <span className={`relative block ${className ?? ''}`}>
      <Input
        ref={input}
        id={id}
        role="combobox"
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={open && active >= 0 ? `${listId}-${active}` : undefined}
        autoComplete="off"
        className="pr-8 text-ellipsis"
        disabled={disabled}
        required={required && !value}
        placeholder={(open && selected?.label) || placeholder || noneLabel}
        value={open ? query : (selected?.label ?? '')}
        onClick={openList}
        onBlur={() => {
          setOpen(false);
          setQuery('');
        }}
        onKeyDown={onKeyDown}
        onChange={(e) => {
          if (!open) openList();
          setQuery(e.target.value);
          setActive(nextEnabled(filterOptions(all, e.target.value), -1, 1));
        }}
      />
      <ChevronDown
        size={16}
        aria-hidden
        className="pointer-events-none absolute top-1/2 right-2.5 -translate-y-1/2 text-muted"
      />
      {open && (
        <ul
          id={listId}
          role="listbox"
          style={place}
          className="fixed z-[60] overflow-y-auto rounded-md border border-rule bg-sheet py-1 shadow-lg"
          // Clicar na lista não tira o foco do campo nem aciona o rótulo em volta dele.
          onMouseDown={(e) => e.preventDefault()}
          onClick={(e) => e.preventDefault()}
        >
          {shown.length === 0 && (
            <li className="px-3 py-2 text-sm text-muted">{t('common.noResults')}</li>
          )}
          {groupOptions(shown).map((g, groupAt) => (
            <li key={groupAt} role="presentation">
              <ul role="group" aria-label={g.group ?? undefined}>
                {g.group && (
                  <li
                    role="presentation"
                    className="px-3 pt-2 pb-1 text-xs font-semibold text-muted"
                  >
                    {g.group}
                  </li>
                )}
                {g.options.map((option) => {
                  const at = shown.indexOf(option);
                  return (
                    <li
                      key={option.id || 'none'}
                      id={`${listId}-${at}`}
                      role="option"
                      aria-selected={option.id === value}
                      aria-disabled={option.disabled || undefined}
                      className={`flex cursor-pointer flex-wrap items-baseline justify-between gap-x-2 px-3 py-1.5 text-sm ${
                        at === active ? 'bg-brand-soft' : ''
                      } ${option.disabled ? 'cursor-not-allowed text-muted' : ''}`}
                      onMouseEnter={() => !option.disabled && setActive(at)}
                      onClick={() => choose(option)}
                    >
                      <span className="min-w-0 break-words">{option.label}</span>
                      {option.tag && <Badge tone={option.tag.tone}>{option.tag.label}</Badge>}
                      {option.detail && (
                        <span className="w-full text-xs text-muted">{option.detail}</span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </li>
          ))}
        </ul>
      )}
    </span>
  );
}
